import { useLocation } from '@reach/router'
import { AnimatePresence, motion } from 'framer-motion'
import { Link, graphql, navigate, useStaticQuery } from 'gatsby'
import React, { useEffect, useState } from 'react'
import { useWindowScroll } from 'react-use'
import { useScrollDirection } from 'react-use-scroll-direction'
import { getLangBasePath, getPathPrefix } from '../../helpers/routing'
import { useLang } from '../../hooks/useLang'
import { useNavHover } from '../../hooks/useNavHover'
import { useSiteId } from '../../hooks/useSiteId'
import Logo from '../atoms/Logo'
import LogoWhite from '../atoms/LogoWhite'
import MobileMenu from '../molecules/MobileMenu'
import NavGroup from '../molecules/NavGroup'
import NavLink from '../molecules/NavLink'

const getActiveRootId = (prefix, pathname, nav) =>
  nav.find(({ target }) => {
    if (pathname === prefix) {
      return null
    }

    const targetUrl = `${prefix}/${target?.element?.slug}`

    return pathname.startsWith(targetUrl) || targetUrl.startsWith(pathname)
  })?.id

const Header = ({ variant, inverted, overlay, alternates, onOpenTool }) => {
  const location = useLocation()
  const siteId = useSiteId()
  const locale = useLang()
  const lang = getLangBasePath(location)
  const prefix = getPathPrefix(lang)
  const { y: scrollY } = useWindowScroll()
  const { isScrollingUp, isScrollingDown } = useScrollDirection()
  const [isOverlayVisible, setOverlayVisible] = useState(false)
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false)

  const {
    craftContent: { globalSets: sites },
  } = useStaticQuery(graphql`
    query HeaderNavQuery {
      craftContent {
        globalSets(handle: "headerNav", siteId: [1, 2, 3]) {
          ... on CraftContent_headerNav_GlobalSet {
            navigation {
              ... on CraftContent_navigation_rootLevelLink_BlockType {
                __typename
                id
                target {
                  ...linkFragment
                }
                emphasize
              }
              ... on CraftContent_navigation_childLevelLink_BlockType {
                __typename
                id
                target {
                  ...linkFragment
                }
              }
            }
            siteId
          }
        }
      }
    }
  `)
  const nav = sites.find(({ siteId: id }) => id === siteId).navigation
  const activeRootId = getActiveRootId(prefix, location.pathname, nav)
  const { getHoverStart, getHoverEnd } = useNavHover(nav)

  useEffect(() => {
    if (scrollY < 100 && isOverlayVisible) {
      setOverlayVisible(false)
      return
    }

    if (isScrollingDown && isOverlayVisible) {
      setOverlayVisible(false)
      return
    }

    if (isScrollingUp && !isOverlayVisible) {
      setOverlayVisible(true)
    }
  }, [isScrollingUp, isScrollingDown, scrollY])

  const shouldShowOverlay = overlay && isOverlayVisible
  const subnav = nav.slice(
    getHoverStart(activeRootId),
    getHoverEnd(activeRootId),
  )

  return (
    <>
      <header
        className={`${inverted && 'bg-black'} z-50 overflow-hidden ${
          !overlay && !inverted && 'mb-16'
        } ${
          overlay &&
          ' fixed left-0 right-0 top-0 shadow bg-gray-1 hidden md:block transition transform -translate-y-full duration-300'
        } ${shouldShowOverlay && 'translate-y-0'} ${
          variant !== 'standard' && 'pb-12'
        }`}
      >
        <div className="relative pt-10 space-y-2">
          <div className="container flex items-center justify-between">
            <Link to={prefix === '' ? '/' : prefix}>
              {inverted ? <LogoWhite /> : <Logo />}
            </Link>
          </div>

          {variant === 'standard' && (
            <nav
              className={`relative hidden pt-6 md:block ${
                overlay && subnav.length > 0 && 'pb-7'
              }`}
            >
              <ul
                className={`container flex items-center font-bold pb-6 ${
                  inverted ? 'text-white' : 'text-black'
                } text-[13px] leading-snug`}
              >
                {nav
                  .filter(
                    ({ __typename }) =>
                      __typename ===
                      'CraftContent_navigation_rootLevelLink_BlockType',
                  )
                  .map(({ id, target, emphasize }) => (
                    <NavGroup
                      key={id}
                      id={id}
                      target={target}
                      emphasize={emphasize}
                      inverted={inverted}
                    />
                  ))}
              </ul>
              {activeRootId !== null && subnav.length > 0 && (
                <div className="max-w-full overflow-x-auto h-7 no-scrollbars">
                  <ul
                    key={activeRootId}
                    className="container z-10 flex items-center space-x-6 text-[13px] leading-[17px] font-bold"
                  >
                    {subnav.map(({ id: childId, target: childTarget }) => (
                      <li key={childId}>
                        <NavLink inverted={inverted} target={childTarget} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </nav>
          )}

          {!overlay && (
            <motion.div
              className="fixed bottom-0 right-0 z-40 hidden overflow-hidden md:block"
              animate={{ y: scrollY > 1500 ? '100%' : 0 }}
            >
              <motion.select
                className="z-40 text-xs text-white bg-black border-0 u-lang-select"
                animate={{ y: scrollY > 400 ? 32 : 0 }}
                transition={{ type: 'ease' }}
                value={
                  alternates && locale in alternates
                    ? alternates[locale]
                    : `/${locale}`
                }
                onChange={e => {
                  window.localStorage.setItem('langOverride', '1')
                  navigate(e.target.value)
                }}
              >
                <option
                  value={
                    alternates && 'de' in alternates ? alternates.de : '/de'
                  }
                >
                  Deutsch
                </option>
                <option
                  value={
                    alternates && 'fr' in alternates ? alternates.fr : '/fr'
                  }
                >
                  Français
                </option>
                <option
                  value={
                    alternates && 'it' in alternates ? alternates.it : '/it'
                  }
                >
                  Italiano
                </option>
              </motion.select>
            </motion.div>
          )}
        </div>
      </header>
      {variant === 'standard' && (
        <button
          type="button"
          className="fixed top-6 right-5.5 md:hidden w-4.5 h-3 z-40"
          onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
        >
          <span
            className={`absolute top-0 left-0 right-0 h-0.5 ${
              inverted ? 'bg-white' : 'bg-black'
            } rounded-full`}
          />
          <span
            className={`absolute top-[5px] left-0 right-0 h-0.5 ${
              inverted ? 'bg-white' : 'bg-black'
            } rounded-full`}
          />
          <span
            className={`absolute bottom-0 left-0 right-0 h-0.5 ${
              inverted ? 'bg-white' : 'bg-black'
            } rounded-full`}
          />
        </button>
      )}

      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            nav={nav}
            onOpenTool={onOpenTool}
            alternates={alternates}
            onClose={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  )
}

export default Header
